import { z } from "zod";
import { TIME_GRAINS } from "./model";

export const TimeGrainSchema = z.enum(TIME_GRAINS);
export const FormatSchema = z.enum(["currency", "number", "integer", "percent", "duration"]);
/**
 * A formula's format: any format, or `ratio`, a fraction (0.25) shown as a percent (25%). `percent`
 * expects the value already × 100 (`tips / net_sales * 100`); the planner applies a ratio's × 100.
 */
export const FormulaFormatSchema = z.enum([...FormatSchema.options, "ratio"]);

const refPattern = /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;
export const RefSchema = z.string().regex(refPattern, "Expected a reference like `model.field`");

export const FILTER_OPS = [
  "eq", "neq", "in", "not_in", "gt", "gte", "lt", "lte", "between", "contains", "is_null", "not_null",
] as const;
export type FilterOp = (typeof FILTER_OPS)[number];

export const FilterSchema = z.object({
  ref: RefSchema,
  op: z.enum(FILTER_OPS),
  values: z.array(z.union([z.string().max(200), z.number(), z.boolean()])).max(500).default([]),
});
export type Filter = z.infer<typeof FilterSchema>;

/** Most filters one filtered column can carry. */
export const MAX_COLUMN_FILTERS = 10;

/**
 * A hidden column is worked out, and formulas, the sort and row filters can use it, but it isn't
 * shown or downloaded (the servers' sales behind a bartender pool split).
 */
const HiddenSchema = z.boolean().optional()
  .describe("Work this column out for formulas, sort and row filters, but don't show it.");

export const MetricSelectionSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("metric"),
    ref: RefSchema,
    /**
     * A filtered column: the metric counted only for rows matching these filters ("Alcohol sales" is
     * Net sales where Account is one of Beer, Liquor, Wine), next to other columns that aren't. Needs
     * `id` and `label`; its key is `filtered.<id>`, which formulas can use. The query's own filters
     * still apply on top.
     */
    filters: z.array(FilterSchema).min(1).max(MAX_COLUMN_FILTERS).optional()
      .describe("Only for a filtered column: count this metric only for rows matching these filters (ANDed). Needs id and label."),
    id: z.string().regex(/^[a-z][a-z0-9_]*$/).optional().describe("Filtered column only: snake_case id; formulas use it as filtered.<id>"),
    label: z.string().min(1).max(80).optional().describe("Filtered column only: its column name, e.g. Alcohol sales"),
    hidden: HiddenSchema,
  }),
  z.object({
    kind: z.literal("formula"),
    id: z.string().regex(/^[a-z][a-z0-9_]*$/),
    label: z.string().min(1).max(80),
    expr: z.string().min(1).max(500),
    format: FormulaFormatSchema.optional(),
    hidden: HiddenSchema,
  }),
]);
export type MetricSelection = z.infer<typeof MetricSelectionSchema>;
export type MetricSelectionInput = z.input<typeof MetricSelectionSchema>;

/** A saved or typed metric selection with its defaults filled in (a filtered column's empty `values`). */
export const toMetricSelection = (m: MetricSelectionInput): MetricSelection =>
  m.kind === "metric" && m.filters ? { ...m, filters: m.filters.map((f) => ({ ...f, values: f.values ?? [] })) } : (m as MetricSelection);

export const DimensionSelectionSchema = z.object({
  ref: RefSchema,
  grain: TimeGrainSchema.optional(),
});
export type DimensionSelection = z.infer<typeof DimensionSelectionSchema>;


/** Most page-level filters a dashboard adds to each of its widgets. */
export const MAX_DASHBOARD_FILTERS = 20;

/**
 * A page-level filter from a drill-down click, applied to every widget on the dashboard that can
 * take it. `pack` is the data pack its field belongs to (absent: the workspace's first).
 */
export const DashboardFilterSchema = FilterSchema.extend({
  pack: z.string().regex(/^[a-z0-9_]+$/).max(64).optional(),
});
export type DashboardFilter = z.infer<typeof DashboardFilterSchema>;

const isoDateTime = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?)?$/, "Expected YYYY-MM-DD or YYYY-MM-DD HH:MM[:SS]");

/**
 * Venue-local dates, both included: Oct 19 to Oct 25 is `2026-10-19` to `2026-10-25`. An end with a
 * time (`2026-10-25 14:00`) is an exact instant and stays exclusive.
 */
export const TimeRangeSchema = z.object({ start: isoDateTime, end: isoDateTime });
export type TimeRange = z.infer<typeof TimeRangeSchema>;

/**
 * The last day a range covers, for labels. A date-only end is that day. An end with a time is
 * exclusive, so one at midnight (`2026-10-26 00:00`) stops the day before it, and one later in the
 * day (`2026-10-25 14:00`) is partway through that day.
 */
export function lastDayOf(r: { start: string; end: string }): string {
  const day = r.end.slice(0, 10);
  if (r.end.length <= 10 || !/^00:00(:00)?$/.test(r.end.slice(11))) return day;
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  const prev = d.toISOString().slice(0, 10);
  return prev < r.start.slice(0, 10) ? r.start.slice(0, 10) : prev;
}

/**
 * Relative ranges, resolved on the server against each location's own current
 * (fiscal) day, never the viewer's browser clock. "Last N days" excludes today; "last week" is
 * the previous full Monday–Sunday week.
 */
export const TIME_PRESETS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "last_7_days", label: "Last 7 days" },
  { id: "last_week", label: "Last week" },
  { id: "last_30_days", label: "Last 30 days" },
  { id: "last_90_days", label: "Last 90 days" },
  { id: "month_to_date", label: "Month to date" },
  { id: "last_month", label: "Last month" },
] as const;
export type TimePreset = (typeof TIME_PRESETS)[number]["id"];
export const TimePresetSchema = z.enum(TIME_PRESETS.map((p) => p.id) as [TimePreset, ...TimePreset[]]);

/**
 * The time part of a widget query: a preset, resolved by the server in each
 * location's own time zone, or a custom absolute range that applies the same
 * dates to every location.
 */
export type TimeSelection =
  | { timePreset: TimePreset; timeRange?: undefined }
  | { timeRange: TimeRange; timePreset?: undefined };

export const asPreset = (p: string | undefined): TimePreset =>
  (TIME_PRESETS.find((x) => x.id === p)?.id ?? "last_30_days");

/** A dashboard's saved date range as a selection. A saved custom range wins over the preset. */
export const asSelection = (f: { timePreset?: string; timeRange?: TimeRange } | undefined): TimeSelection =>
  f?.timeRange ? { timeRange: f.timeRange } : { timePreset: asPreset(f?.timePreset) };

export const MAX_COMPARE_PERIODS = 6;

/**
 * A dashboard input: a named number a widget's formulas use as `input.<id>`
 * (`filtered.alcohol_sales * input.bartender_rate`). The widget declares each one with a default, so
 * it runs on its own; on a dashboard, the box for that id supplies the value. `format` only says how
 * the box shows and reads the number: a percent input stores a fraction (3% is 0.03), so formulas
 * multiply by it directly.
 */
export const INPUT_FORMATS = ["number", "currency", "percent"] as const;
export type InputFormat = (typeof INPUT_FORMATS)[number];
/** Largest input value either way. */
export const MAX_INPUT_ABS = 1e9;
/** Most inputs one widget declares. */
export const MAX_INPUTS = 10;
/** Most input values one request or dashboard carries (several widgets' inputs). */
export const MAX_INPUT_VALUES = 50;
const INPUT_ID = /^[a-z][a-z0-9_]*$/;
const inputNumber = z.number().finite().min(-MAX_INPUT_ABS).max(MAX_INPUT_ABS);

export const InputSchema = z.object({
  id: z.string().regex(INPUT_ID).max(64).describe("snake_case id; formulas use it as input.<id>"),
  label: z.string().min(1).max(80).describe("The box's name on the dashboard, e.g. Bartender tip-out"),
  format: z.enum(INPUT_FORMATS).default("number").describe("percent stores a fraction: 3% is 0.03"),
  default: inputNumber.describe("The value used until the dashboard owner saves another. A percent is a fraction (0.03 for 3%)."),
});
export type InputDef = z.infer<typeof InputSchema>;
export type InputDefInput = z.input<typeof InputSchema>;

/** Input values by id: what a dashboard saves and what a query request sends. */
export const InputValuesSchema = z
  .record(z.string().regex(INPUT_ID).max(64), inputNumber)
  .refine((v) => Object.keys(v).length <= MAX_INPUT_VALUES, `At most ${MAX_INPUT_VALUES} input values`);
export type InputValues = z.infer<typeof InputValuesSchema>;

/** Why `value` can't be this input's value, or null when it can. */
export function inputValueProblem(format: InputFormat, value: number): string | null {
  if (!Number.isFinite(value) || Math.abs(value) > MAX_INPUT_ABS) return "must be a number between −1,000,000,000 and 1,000,000,000";
  if (format === "percent" && (value < 0 || value > 1)) return "must be a percent between 0% and 100%";
  return null;
}

/** An input value as its box shows it: 0.03 → "3%", 12.5 → "$12.50". */
export function formatInputValue(format: InputFormat, value: number): string {
  if (format === "percent") return `${Math.round(value * 100 * 1e6) / 1e6}%`;
  if (format === "currency")
    return `${value < 0 ? "−" : ""}$${Math.abs(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return value.toLocaleString("en-US", { maximumFractionDigits: 6 });
}

/** Model names formula refs use for the widget's own things: `filtered.<id>` columns and `input.<id>` inputs. */
export const WIDGET_REF_MODELS = ["filtered", "input"] as const;
/** A formula ref to one of the widget's own columns or inputs, not to a pack metric. */
export const isWidgetRef = (ref: string) => WIDGET_REF_MODELS.some((m) => ref.startsWith(`${m}.`));
/** The input ids a formula names (`input.<id>`), once each. A plain scan, so a formula that doesn't parse still lists them. */
export function inputRefsIn(expr: string): string[] {
  return [...new Set([...expr.matchAll(/(?<![a-z0-9_.])input\.([a-z][a-z0-9_]*)/g)].map((m) => m[1]!))];
}

export const SortSchema = z.object({ key: z.string(), dir: z.enum(["asc", "desc"]) });

/** Conditions a row filter can put on a column's number. */
export const ROW_FILTER_OPS = ["gt", "gte", "lt", "lte", "eq", "neq", "between", "not_null", "is_null"] as const;
/** Most row filters one widget can have. */
export const MAX_ROW_FILTERS = 10;
/**
 * "Only show rows where…": a condition on one of the widget's columns (its key: a metric ref,
 * `filtered.<id>` or `formula.<id>`, hidden ones too), checked after everything is worked out. So the
 * rows it hides still count toward `total()`, and it never changes what a column counts.
 */
export const RowFilterSchema = z.object({
  key: z.string().max(200).describe("A column of this widget: a metric ref, filtered.<id> or formula.<id>"),
  op: z.enum(ROW_FILTER_OPS),
  values: z.array(z.number().finite()).max(2).default([]).describe("The number(s) to compare with, as the column shows them (25 for 25%)"),
});
export type RowFilter = z.infer<typeof RowFilterSchema>;
export type RowFilterInput = z.input<typeof RowFilterSchema>;

export const SemanticQuerySchema = z.object({
  /**
   * The workspace data pack every ref belongs to ("gotab", "fixture"). Absent means the workspace's
   * first pack, so a query never spans packs and queries saved before there were several still run.
   */
  pack: z.string().regex(/^[a-z0-9_]+$/).max(64).optional(),
  metrics: z.array(MetricSelectionSchema).min(1).max(50),
  dimensions: z.array(DimensionSelectionSchema).max(20).default([]),
  filters: z.array(FilterSchema).max(50).default([]),
  /** Absolute range; the same dates apply to every location. Mutually exclusive with `timePreset`. */
  timeRange: TimeRangeSchema.optional(),
  /** Relative range, resolved per location. */
  timePreset: TimePresetSchema.optional(),
  compareTo: z.enum(["previous_period", "previous_year"]).optional(),
  /** How many periods back to compare with (1 = just the one before). Needs `compareTo`. */
  comparePeriods: z.number().int().min(1).max(MAX_COMPARE_PERIODS).optional(),
  /**
   * Show additive metrics as an average per day instead of a total: each row is divided by the
   * number of days in the range that fall into it, so a Monday bucket is divided by the number of
   * Mondays. Evens out groupings whose buckets cover different numbers of days. Needs a date range.
   */
  averagePer: z.enum(["day"]).optional(),
  /**
   * Work the formulas out for each business day separately, then add each row's days up: a pool is
   * split by each day's hours, not the whole range's. Every column has to add up. Needs a date range;
   * not with `averagePer`.
   */
  eachDay: z.boolean().optional()
    .describe("Work formulas out for each business day, then add the days up (e.g. a daily pool split by that day's hours)"),
  /** "Only show rows where…": conditions on the widget's own columns, after totals and formulas. */
  rowFilters: z.array(RowFilterSchema).max(MAX_ROW_FILTERS).optional(),
  /**
   * The dashboard inputs this widget's formulas use (`input.<id>`), each with the default it runs
   * with when no value is sent.
   */
  inputs: z.array(InputSchema).max(MAX_INPUTS).optional(),
  sort: z.array(SortSchema).max(5).default([]),
  limit: z.number().int().positive().optional(),
  /**
   * Apply `limit` to groups instead of rows: keep the `limit` groups of these dimension keys
   * with the highest (or lowest) total of the metric `by`, and every row inside them. A pivot
   * uses it so "top 20" means the 20 products with the biggest Total, each with all its hours.
   */
  limitBy: z.object({
    dimensions: z.array(z.string()).min(1).max(20),
    by: z.string(),
    dir: z.enum(["asc", "desc"]).default("desc"),
  }).optional(),
});
export type SemanticQuery = z.infer<typeof SemanticQuerySchema>;
export type SemanticQueryInput = z.input<typeof SemanticQuerySchema>;

/** Stable key used for result columns and sorting. */
export function metricKey(m: MetricSelectionInput): string {
  if (m.kind === "formula") return `formula.${m.id}`;
  return m.filters?.length ? `filtered.${m.id}` : m.ref;
}

/** A metric selection with its own filters (a filtered column). */
export const isFilteredMetric = <M extends MetricSelectionInput>(m: M): m is M & { kind: "metric"; filters: unknown[] } =>
  m.kind === "metric" && !!m.filters?.length;
export function dimensionKey(d: DimensionSelection): string {
  return d.grain ? `${d.ref}:${d.grain}` : d.ref;
}
